import React, { useContext } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { AuthContext } from '../App';
import { Button } from './ui/button';
// Avatar components removed - using simple div with img
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from './ui/dropdown-menu';
import { LogOut, Settings, User, BookOpen, Calendar, GraduationCap, Briefcase, Gift, Sparkles } from 'lucide-react';
import CreditPurchaseModal from './CreditPurchaseModal';
import { isMobileApp, openWebappWithAuth } from '../utils/platform';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Navbar = () => {
  // Use white logo on mobile app, colored logo on web
  const logoImg = isMobileApp() 
    ? "/nerd-nation-logo-white.png"
    : "https://customer-assets.emergentagent.com/job_collegeai-hub/artifacts/3mtqhnui_nerd%20indians%20%28200%20x%20200%20px%29%286%29.png?v=" + Date.now();
  
  const { user, handleLogout, openAuth } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [credits, setCredits] = React.useState(0);
  const [showCreditModal, setShowCreditModal] = React.useState(false);

  const isActive = (path) => location.pathname === path;
  
  // Fetch credit balance
  React.useEffect(() => {
    if (user) {
      fetchCreditBalance();
    }
  }, [user]);
  
  const fetchCreditBalance = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${BACKEND_URL}/api/credits/balance`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      if (data.success) {
        setCredits(data.credits || 0);
      }
    } catch (error) {
      console.error('Failed to fetch credits:', error);
    }
  };
  
  // Listen for credit update events
  React.useEffect(() => {
    const handleCreditUpdate = () => {
      fetchCreditBalance();
    };
    
    window.addEventListener('creditUpdated', handleCreditUpdate);
    return () => window.removeEventListener('creditUpdated', handleCreditUpdate);
  }, []);
  
  // Poll credits every 10 seconds for live updates
  React.useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(() => {
      fetchCreditBalance();
    }, 10000); // Every 10 seconds
    
    return () => clearInterval(interval);
  }, [user]);
  
  // Refresh credits when page becomes visible
  React.useEffect(() => {
    if (!user) return;
    
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        fetchCreditBalance();
      }
    };
    
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [user]);
  
  // Navigation helper functions
  const goHome = () => {
    navigate('/');
  };
  
  // Dashboard is now always at "/" so update home navigation
  const isHomePage = location.pathname === '/';

  return (
    <nav className={`${isMobileApp() ? 'bg-[#1a1a2e]' : 'bg-white/95'} backdrop-blur-xl border-b ${isMobileApp() ? 'border-gray-700/30' : 'border-gray-200/30'} sticky top-0 z-50 shadow-sm`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-14">
          {/* Logo */}
          <div className="flex items-center space-x-3">
            <button 
              onClick={goHome}
              className="flex items-center hover:opacity-80 transition-opacity"
              data-testid="home-logo"
            >
              {/* Mobile logo (square format, prominent) - use white version in mobile app */}
              <img 
                src={isMobileApp() ? "/nerd-nation-logo-white.png" : "https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/wpzfhpfx_Time%284%29.png"} 
                alt="Nerd Nation"
                className="block sm:hidden w-14 h-14 object-contain"
                onError={(e) => { console.error('Mobile logo failed to load'); }} loading="lazy" decoding="async" />
              {/* Desktop logo (horizontal format) - use white version in mobile app */}
              <img 
                src={isMobileApp() ? logoImg : "https://customer-assets.emergentagent.com/job_736cdda7-e974-4fad-aa62-3a758e69e883/artifacts/2zp255co_Time%2851%29.png"} 
                alt="Nerd Nation"
                className="hidden sm:block w-20 h-20 object-contain"
                onError={(e) => { console.error('Desktop logo failed to load'); }} loading="lazy" decoding="async" />
            </button>
          </div>

          {/* Navigation Links - Minimal clean design */}
          <div className="hidden md:flex items-center space-x-8">
            {/* No navigation items here - keeping it clean */}
          </div>

          {/* User Menu */}
          <div className="flex items-center gap-2">
            {/* Credit Balance & Buy Button */}
            {user && (
              <>
                <div style={{ height: '34px' }} className="flex items-center gap-1.5 px-2.5 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-300 shadow-sm">
                  <div className="relative">
                    <svg className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <circle cx="10" cy="10" r="9" fill="currentColor" opacity="0.3"/>
                    </svg>
                    <svg className="w-2.5 h-2.5 text-yellow-500 absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2" fill="currentColor" viewBox="0 0 20 20">
                      <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"/>
                    </svg>
                  </div>
                  <span
                    className="font-bold text-amber-900 whitespace-nowrap"
                    style={{
                      fontSize: String(credits).length <= 3 ? '14px'
                               : String(credits).length <= 5 ? '11px'
                               : '9px'
                    }}
                  >{credits}</span>
                </div>
                <button
                  onClick={() => {
                    if (isMobileApp()) {
                      openWebappWithAuth('/'); // Open homepage with auto-login
                    } else {
                      navigate('/buy-credits');
                    }
                  }}
                  style={{ '--button-primary-height': '34px' }}
                  className="flex items-center justify-center bg-gradient-to-r from-amber-600 to-orange-600 text-white text-[10px] font-semibold px-[9px] rounded-md shadow-md whitespace-nowrap navbar-credits-btn"
                >
                  {isMobileApp() ? 'Get AI Credits' : 'Buy AI Credits'}
                </button>
              </>
            )}
            
            {user ? (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="cursor-pointer hover:opacity-80 transition-opacity focus:outline-none flex-shrink-0">
                    <div className="w-10 h-10 rounded-full overflow-hidden bg-gradient-to-br from-blue-700 to-blue-800 flex items-center justify-center shadow-md">
                      {user.avatar_url ? (
                        <img 
                          src={user.avatar_url.startsWith('data:') ? user.avatar_url : `${process.env.REACT_APP_BACKEND_URL}${user.avatar_url}`}
                          alt={user.full_name}
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            e.target.style.display = 'none';
                          }} loading="lazy" decoding="async" />
                      ) : (
                        <span className="text-white text-sm font-semibold">
                          {user.full_name?.charAt(0)?.toUpperCase() || 'U'}
                        </span>
                      )}
                    </div>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  {/* User Info Header */}
                  <div className="px-2 py-3 border-b border-gray-100">
                    <p className="font-medium text-gray-900 text-sm">{user.full_name}</p>
                    <p className="text-xs text-gray-500 truncate">{user.email}</p>
                  </div>
                  
                  <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="profile-menu-item">
                    <User className="mr-2 h-4 w-4" />
                    Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/job-board')} data-testid="jobs-menu-item">
                    <Briefcase className="mr-2 h-4 w-4" />
                    My Jobs
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/events')} data-testid="events-menu-item">
                    <Sparkles className="mr-2 h-4 w-4" />
                    Events
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-events')} data-testid="my-events-menu-item">
                    <Calendar className="mr-2 h-4 w-4" />
                    My Events
                  </DropdownMenuItem>
                  
                  {/* Show Employer Dashboard for approved employers, admin, and subadmin */}
                  {((user?.profile?.study_year === 'Employer' && user?.employer_status === 'approved') || 
                    user?.email === 'shifracs@gmail.com' || 
                    user?.email === 'Advisor@mednerd.in') && (
                    <DropdownMenuItem onClick={() => navigate('/employer-dashboard')} data-testid="employer-dashboard-menu-item">
                      <Briefcase className="mr-2 h-4 w-4" />
                      Employer Dashboard
                    </DropdownMenuItem>
                  )}

                  {/* Organizer Dashboard — admin/subadmin only */}
                  {(user?.email === 'shifracs@gmail.com' || user?.email === 'Advisor@mednerd.in' || user?.role === 'admin' || user?.role === 'subadmin') && (
                    <DropdownMenuItem onClick={() => navigate('/organizer-dashboard')} data-testid="organizer-dashboard-menu-item">
                      <Sparkles className="mr-2 h-4 w-4" />
                      Organizer Dashboard
                    </DropdownMenuItem>
                  )}
                  
                  <DropdownMenuSeparator />
                  
                  {/* LMS Navigation - Now unlocked for all users */}
                  <DropdownMenuItem onClick={() => navigate('/courses')} data-testid="courses-menu-item">
                    <BookOpen className="mr-2 h-4 w-4" />
                    Browse Courses
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/my-learning')} data-testid="my-learning-menu-item">
                    <GraduationCap className="mr-2 h-4 w-4" />
                    My Learning
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate('/calendar')} data-testid="calendar-menu-item">
                    <Calendar className="mr-2 h-4 w-4" />
                    Class Calendar
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  {/* Student Offers Link */}
                  <DropdownMenuItem onClick={() => navigate('/student-offers')} data-testid="student-offers-menu-item">
                    <Gift className="mr-2 h-4 w-4" />
                    Student Offers
                  </DropdownMenuItem>

                  {/* Scholarships Link */}
                  <DropdownMenuItem onClick={() => navigate('/scholarships')} data-testid="scholarships-menu-item">
                    <GraduationCap className="mr-2 h-4 w-4" />
                    Scholarships
                  </DropdownMenuItem>
                  
                  <DropdownMenuSeparator />
                  
                  {/* Admin links - visible to admin and sub_admin, but NOT on mobile app */}
                  {!isMobileApp() && (user?.email === 'shifracs@gmail.com' || 
                    user?.role === 'admin' || 
                    user?.role === 'subadmin' || 
                    user?.lms_role === 'admin' || 
                    user?.lms_role === 'sub_admin') && (
                    <>
                      <DropdownMenuItem onClick={() => navigate('/admin')} data-testid="admin-menu-item">
                        <Settings className="mr-2 h-4 w-4" />
                        Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/lms-admin')} data-testid="lms-admin-menu-item">
                        <Settings className="mr-2 h-4 w-4" />
                        LMS Admin
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate('/admin-offers')} data-testid="admin-offers-menu-item">
                        <Settings className="mr-2 h-4 w-4" />
                        Offers Admin
                      </DropdownMenuItem>
                    </>
                  )}
                  <DropdownMenuItem onClick={handleLogout} data-testid="logout-menu-item">
                    <LogOut className="mr-2 h-4 w-4" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            ) : (
              <div className="flex items-center space-x-3">
                <Button
                  onClick={() => openAuth('login')}
                  className="bg-gradient-to-r from-blue-700 to-blue-800 hover:from-blue-800 hover:to-blue-900"
                  data-testid="signin-button"
                >
                  Sign In
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Credit Purchase Modal */}
      <CreditPurchaseModal
        isOpen={showCreditModal}
        onClose={() => setShowCreditModal(false)}
        onSuccess={fetchCreditBalance}
      />
    </nav>
  );
};

export default Navbar;